<template>
  <div>
    <div v-if="isLoadingSummary" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading summary data...</p>
    </div>

    <div v-if="!isLoadingSummary" class="idp-summary-wrapper mb-4">
      <div class="idp-overview-card">
        <div class="idp-summary-header">
          <div>
            <span class="summary-eyebrow">This Year</span>
            <h5 class="mb-0">IDP Enrollment Summary</h5>
          </div>
          <div class="summary-rate">
            <strong>{{ currentEnrollmentPercentage }}%</strong>
            <span>enrolled vs TNA approved</span>
          </div>
        </div>

        <div class="progress mb-3" style="height: 18px">
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="`width: ${currentEnrollmentPercentage}%;`"
            :aria-valuenow="currentEnrollmentPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ currentEnrollmentPercentage }}%
          </div>
        </div>

        <div class="summary-card-grid">
          <div class="status-card bg-warning">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-target"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">TNA Approved</p>
              <h3 class="mb-0">{{ currentSummary.tna_approved }}</h3>
            </div>
          </div>

          <div class="status-card bg-danger">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-clock"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Need Approval</p>
              <h3 class="mb-0">{{ currentSummary.need_approval }}</h3>
            </div>
          </div>

          <div class="status-card bg-teal">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-check-circle"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Enrolled</p>
              <h3 class="mb-0">{{ currentSummary.enrolled }}</h3>
            </div>
          </div>

          <div class="status-card bg-success">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-check"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Completed</p>
              <h3 class="mb-0">{{ currentSummary.completed }}</h3>
            </div>
          </div>
        </div>
      </div>

      <div class="history-card">
        <span class="summary-eyebrow">Last Year</span>
        <h5 class="mb-3">IDP Enrollment</h5>
        <div class="history-list">
          <div>
            <span>TNA Approved</span>
            <strong>{{ previousSummary.tna_approved }}</strong>
          </div>
          <div>
            <span>Need Approval</span>
            <strong>{{ previousSummary.need_approval }}</strong>
          </div>
          <div>
            <span>Enrolled</span>
            <strong>{{ previousSummary.enrolled }}</strong>
          </div>
          <div>
            <span>Completed</span>
            <strong>{{ previousSummary.completed }}</strong>
          </div>
        </div>
        <div class="history-progress">
          <div :style="`width: ${previousEnrollmentPercentage}%;`"></div>
        </div>
        <small>{{ previousEnrollmentPercentage }}% enrolled vs TNA approved</small>
      </div>

      <div class="idp-roadmap-card">
        <div class="idp-summary-header">
          <div>
            <span class="summary-eyebrow">Roadmap</span>
            <h5 class="mb-0">Proses Approval Enrollment IDP</h5>
          </div>
        </div>

        <div class="roadmap-lanes">
          <div class="roadmap-lane">
            <div class="lane-heading">
              <span>Ikatan Dinas</span>
            </div>
            <div class="idp-roadmap is-service-bond">
              <div v-for="step in serviceBondSteps" :key="step.label" class="roadmap-step">
                <div class="roadmap-marker">
                  <i :class="step.icon"></i>
                </div>
                <span>{{ step.label }}</span>
              </div>
            </div>
          </div>

          <div class="roadmap-lane">
            <div class="lane-heading">
              <span>Non Ikatan Dinas</span>
            </div>
            <div class="idp-roadmap is-regular">
              <div v-for="step in regularSteps" :key="step.label" class="roadmap-step">
                <div class="roadmap-marker">
                  <i :class="step.icon"></i>
                </div>
                <span>{{ step.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Summary",
  props: {
    isLoadingSummary: {
      type: Boolean,
      default: false,
    },
    summaryData: {
      type: Object,
      default: () => ({
        previous: { plan: 0, approved: 0, completed: 0, percentage: 0 },
        current: { plan: 0, approved: 0, completed: 0, percentage: 0 },
      }),
    },
  },
  computed: {
    currentSummary() {
      return this.normalizeSummary(this.summaryData?.current);
    },
    previousSummary() {
      return this.normalizeSummary(this.summaryData?.previous);
    },
    currentEnrollmentPercentage() {
      return this.calculateEnrollmentPercentage(this.currentSummary);
    },
    previousEnrollmentPercentage() {
      return this.calculateEnrollmentPercentage(this.previousSummary);
    },
    serviceBondSteps() {
      return [
        { label: "Manager", icon: "fe fe-user-check" },
        { label: "HR", icon: "fe fe-users" },
        { label: "KTT", icon: "fe fe-shield" },
        { label: "Deputy COO", icon: "fe fe-briefcase" },
        { label: "COO", icon: "fe fe-award" },
        { label: "Enrolled", icon: "fe fe-check" },
      ];
    },
    regularSteps() {
      return [
        { label: "Manager", icon: "fe fe-user-check" },
        { label: "HSE Training / HR Site", icon: "fe fe-users" },
        { label: "Enrolled", icon: "fe fe-check" },
      ];
    },
  },
  methods: {
    normalizeSummary(summary = {}) {
      const hasStatusBuckets =
        summary?.open !== undefined ||
        summary?.need_approval !== undefined ||
        summary?.enrolled !== undefined ||
        summary?.total !== undefined;

      const normalized = {
        open: Number(summary?.open ?? summary?.plan ?? 0),
        need_approval: Number(summary?.need_approval || 0),
        enrolled:
          Number(summary?.enrolled ?? summary?.approved ?? 0) +
          Number(summary?.in_review || 0) +
          Number(summary?.completed || 0),
        in_review: Number(summary?.in_review || 0),
        completed: Number(summary?.completed || 0),
        total: Number(summary?.total || 0),
        percentage: Number(summary?.percentage || 0),
        hasStatusBuckets,
      };

      normalized.tna_approved = Math.max(
        Number(summary?.tna_approved ?? summary?.approved ?? 0),
        normalized.open + normalized.need_approval + normalized.enrolled
      );

      return normalized;
    },
    calculateEnrollmentPercentage(summary) {
      if (!summary?.hasStatusBuckets) return Math.min(Number(summary?.percentage || 0), 100);

      const tnaApproved = Number(summary?.tna_approved || 0);
      if (!tnaApproved) return 0;

      return Math.min(Math.round((summary.enrolled / tnaApproved) * 100), 100);
    },
  },
};
</script>

<style>
.idp-summary-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 16px;
}

.idp-overview-card,
.history-card,
.idp-roadmap-card {
  border: 1px solid #e6edf7;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.04);
}

.idp-roadmap-card {
  grid-column: 1 / -1;
}

.idp-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-eyebrow {
  display: block;
  color: #90a4c4;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.summary-rate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #5e6b7f;
}

.summary-rate strong {
  color: #b80000;
  font-size: 22px;
  line-height: 1;
}

.summary-rate span {
  font-size: 12px;
  font-weight: 700;
}

.idp-summary-wrapper .summary-card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.history-list {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.history-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #edf1f6;
  padding-bottom: 8px;
}

.history-list span,
.history-card small {
  color: #5e6b7f;
  font-size: 12px;
  font-weight: 700;
}

.history-list strong {
  color: #14213d;
  font-size: 22px;
  line-height: 1;
}

.history-progress {
  height: 8px;
  border-radius: 8px;
  background: #edf1f6;
  overflow: hidden;
  margin-bottom: 8px;
}

.history-progress div {
  height: 100%;
  border-radius: inherit;
  background: #b80000;
}

.roadmap-lanes {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.75fr);
  gap: 14px;
}

.roadmap-lane {
  min-width: 0;
}

.lane-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 10px;
}

.lane-heading span {
  color: #14213d;
  font-size: 14px;
  font-weight: 700;
}

.idp-roadmap {
  position: relative;
  display: grid;
  gap: 10px;
  padding-top: 6px;
}

.idp-roadmap.is-service-bond {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.idp-roadmap.is-regular {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.idp-roadmap::before {
  content: "";
  position: absolute;
  top: 21px;
  left: 14px;
  right: 14px;
  height: 2px;
  background: #dbe4f0;
}

.roadmap-step {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.roadmap-marker {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #08ce79;
  background: #eafff5;
  color: #08a867;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.roadmap-step span {
  display: block;
  color: #14213d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
}

.idp-summary-wrapper .status-card {
  border-radius: 8px;
  padding: 16px;
  min-height: 112px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.idp-summary-wrapper .status-icon {
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.idp-summary-wrapper .status-card p {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;
}

.idp-summary-wrapper .status-card h3 {
  font-size: 30px;
  font-weight: bold;
  margin: 0;
}

.bg-teal {
  background-color: #5bc0be;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.progress-bar {
  font-weight: 600;
  font-size: 14px;
}

@media (max-width: 991.98px) {
  .idp-summary-wrapper,
  .roadmap-lanes,
  .idp-roadmap.is-service-bond,
  .idp-roadmap.is-regular {
    grid-template-columns: 1fr;
  }

  .idp-roadmap::before {
    display: none;
  }

  .summary-rate {
    align-items: flex-start;
  }
}

@media (max-width: 575.98px) {
  .idp-summary-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
